import { createSelector } from 'reselect';

import { BLUEBIRD, NONE } from '../../constants/integrations';
import {
  AI_GIFT_SUGGESTION_ENABLED,
  AUTOMATIONS_ENABLED,
  CRM_SKIP_ALLOWED,
  DEPARTMENT_BUDGET_ENABLED,
  DIGITAL_BALANCE_LIMIT_ENABLED,
  IDLE_RUN_ANONYMIZATION_CHECK,
  PRODUCT_MARKETPLACE_ENABLED,
  USER_BUDGET_ENABLED,
} from '../../constants/organizations';
import { DIGITAL_BUDGET_WARNING_RATE } from '../../constants/shell';
import { DEPT_ADMIN } from '../../constants/users';
import { ISimplifiedOrganization } from '../../types/organizations';
import { IAppState } from '../reducers';
import {
  selectAdminType,
  selectFirebaseClaims,
  selectIsDistributor,
  selectIsSuperAdmin,
  selectOrganizationInfo,
  selectUserDepartmentId,
  selectUserInfo,
} from './auth';

export const selectRootState = (state: IAppState) => state.organizations;

export const selectCanEditOrganization = createSelector(
  selectIsSuperAdmin,
  selectIsDistributor,
  (isSuper, isDistributor) => isSuper || isDistributor,
);

export const selectOrganizations = createSelector(selectRootState, (organizations) =>
  organizations && organizations.items ? organizations.items : undefined,
);

// For uid and name only: the full list when loaded, otherwise the short one fetched on login
export const selectOrganizationList = createSelector(
  selectRootState,
  (organizations) => organizations?.items ?? organizations?.shortItems ?? undefined,
);

export const selectOrganizationById = (id?: string) =>
  createSelector(selectOrganizations, (orgs) =>
    id && orgs && orgs.length ? orgs.find((org) => org.uid === id) : undefined,
  );

export const selectOrganizationDetails = createSelector(selectRootState, (organizations) =>
  organizations ? organizations.organizationDetails : undefined,
);

export const selectOrganizationsIsLoading = createSelector(selectRootState, (organizations) =>
  organizations ? organizations.isLoading : false,
);

export const selectOrganizationsDetailsIsLoading = createSelector(selectRootState, (organizations) =>
  organizations ? organizations.isOrganizationDetailsLoading : false,
);

//#region Current organization data

export const selectCurrentOrganizationId = createSelector(selectFirebaseClaims, (claims) =>
  claims ? (claims.org_id as string) : '',
);

// for super admins only, as users, dept and org admins do not receive the list of orgs
export const selectCurrentOrganization = createSelector(
  selectOrganizationList,
  selectCurrentOrganizationId,
  (organizations, orgId) => {
    return organizations?.find((organization) => organization.uid === orgId);
  },
);

// for all types of users
export const selectCurrentOrganizationName = createSelector(selectUserInfo, (user) => user?.org_name || '');

export const selectCurrentOrganizationData = createSelector(
  selectRootState,
  (organizations) => organizations.current || undefined,
);

export const selectCurrentDepartmentBudgetData = createSelector(selectCurrentOrganizationData, (orgData) =>
  orgData ? orgData.departmentBudget : null,
);

export const selectIsDepartmentBudgetUnlimited = createSelector(
  selectCurrentDepartmentBudgetData,
  (budget) => budget?.available_budget === 0,
);

export const selectCurrentDepartmentRemainingBudget = createSelector(
  selectCurrentDepartmentBudgetData,
  (state: IAppState) => state.shell.currencyRates?.base_curency,
  (budget, fallbackCurrency) => {
    if (!budget) {
      return { amount: 0, budget_currency: fallbackCurrency };
    }

    const { available_budget, used_budget, budget_currency } = budget;

    return { amount: available_budget - used_budget, budget_currency };
  },
);

export const selectCurrentOrganizationDepartmentList = createSelector(
  selectCurrentOrganizationData,
  ({ departmentList }) => departmentList || undefined,
);

export const selectFilteredDepartmentListForUser = createSelector(
  selectCurrentOrganizationDepartmentList,
  selectUserDepartmentId,
  selectAdminType,
  (departmentList, userDepartmentId, adminType) => {
    if (!departmentList) {
      return undefined;
    }

    // If user is DEPT_ADMIN, only show their own department
    if (adminType === DEPT_ADMIN) {
      const userDepartment = userDepartmentId
        ? departmentList.find((dept) => dept.uid === userDepartmentId)
        : undefined;
      return userDepartment ? [userDepartment] : [];
    }

    return departmentList;
  },
);

export const selectCurrentOrganizationOptions = createSelector(
  selectCurrentOrganizationData,
  ({ options }) => options || undefined,
);

export const selectHubspotAppData = createSelector(selectCurrentOrganizationOptions, (org) => org?.hubspot_app || null);

export const selectIsDigitalBalanceLimitEnabled = createSelector(
  selectCurrentOrganizationOptions,
  (options) => options?.[DIGITAL_BALANCE_LIMIT_ENABLED] || false,
);

export const selectIsAutomationsEnabled = createSelector(
  selectCurrentOrganizationOptions,
  (options) => options?.[AUTOMATIONS_ENABLED] || false,
);

export const selectIsProductMarketplaceEnabled = createSelector(
  selectCurrentOrganizationOptions,
  (options) => options?.[PRODUCT_MARKETPLACE_ENABLED] || false,
);

export const selectCurrentOrganizationDigitalBalanceLimit = createSelector(
  selectCurrentOrganizationOptions,
  (options) => options?.digital_balance_limit_value || DIGITAL_BUDGET_WARNING_RATE,
);

export const selectCurrentOrganizationCRMType = createSelector(selectCurrentOrganizationOptions, (options) =>
  options ? options.crm_currently_supports : undefined,
);

export const selectIsCurrentOrganizationSupportedAnyCrm = createSelector(selectCurrentOrganizationCRMType, (crm) =>
  typeof crm !== 'undefined' ? crm !== NONE : undefined,
);

export const selectCurrentOrganizationEmailType = createSelector(selectCurrentOrganizationOptions, (options) =>
  options ? options.email_currently_supports : undefined,
);

export const selectIsCurrentOrganizationSupportedAnyEmail = createSelector(
  selectCurrentOrganizationEmailType,
  (email) => (typeof email !== 'undefined' ? email !== BLUEBIRD : undefined),
);

export const selectIsLoadingDepartmentSummaries = createSelector(
  selectCurrentOrganizationData,
  (organizationData) => organizationData.isDepartmentSummariesLoading,
);

export const selectIsRemovePIILoading = createSelector(
  selectRootState,
  (organizations) => organizations.isOrganizationsPIILoading,
);

export const selectRunAnonymizationCheck = createSelector(
  selectRootState,
  (organizations) => organizations?.runAnonymizationCheck ?? IDLE_RUN_ANONYMIZATION_CHECK,
);

export const selectIsDepartmentBudgetEnabled = createSelector(
  selectCurrentOrganizationData,
  (orgData) => orgData?.options?.[DEPARTMENT_BUDGET_ENABLED],
);

export const selectIsUserBudgetEnabled = createSelector(selectCurrentOrganizationOptions, (options) =>
  Boolean(options?.[USER_BUDGET_ENABLED]),
);

export const selectIsAIGiftSuggestionEnabled = createSelector(
  selectCurrentOrganizationOptions,
  (options) => options?.[AI_GIFT_SUGGESTION_ENABLED],
);

export const selectIsSkipCrmAllowedInOrg = createSelector(
  selectCurrentOrganizationData,
  (orgData) => orgData?.options?.[CRM_SKIP_ALLOWED],
);

export const selectSimplifiedCurrentOrganization = createSelector(
  selectOrganizationInfo,
  selectCurrentOrganizationDepartmentList,
  selectCurrentOrganizationOptions,
  // tslint:disable-next-line:variable-name
  (organizationInfo, org_departments, org_options): ISimplifiedOrganization | undefined => {
    if (!organizationInfo || !org_departments || !org_options) {
      return void 0;
    }

    return {
      ...organizationInfo,
      org_departments,
      org_options,
    };
  },
);

export const selectCurrentOrganizationAutomatedConnections = createSelector(
  selectCurrentOrganizationOptions,
  (options) => options?.automation_connections || [],
);

export const selectAutomatedConnectionsByOrgId = (id?: string) =>
  createSelector(selectOrganizationById(id), (org) => {
    return org?.org_options?.automation_connections || [];
  });

export const selectCurrentOrganizationLogoUrl = createSelector(
  selectOrganizationInfo,
  (orgInfo) => orgInfo?.logo_url || '',
);

export const selectOrgDefaultTemplate = createSelector(
  selectCurrentOrganizationData,
  (orgData) => orgData?.defaultTemplate ?? null,
);

//#endregion
